import { useEffect, useRef } from "react";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

const HISTORY_STATE_KEY = "__ulonkModalToken";

type StackEntry = { token: number; close: () => void };

const modalStack: StackEntry[] = [];
const externallyClosed = new Set<number>();
let nextToken = 1;
let listenerAttached = false;
let programmaticCloseToken: number | null = null;

function ensureListener() {
    if (listenerAttached || typeof window === "undefined") return;
    listenerAttached = true;
    window.addEventListener("popstate", () => {
        if (programmaticCloseToken !== null) {
            programmaticCloseToken = null;
            return;
        }
        const top = modalStack.pop();
        if (top) top.close();
    });
}

export function closeAllOpenModals() {
    if (modalStack.length === 0) return;
    const snapshot = modalStack.slice();
    modalStack.length = 0;
    for (const entry of snapshot) {
        externallyClosed.add(entry.token);
    }
    for (let i = snapshot.length - 1; i >= 0; i--) {
        snapshot[i].close();
    }
}

interface Options {
    enabled?: boolean;
}

export default function useMobileBackClose(
    open: boolean,
    onClose: () => void,
    options: Options = {},
) {
    const { enabled = true } = options;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    useEffect(() => {
        if (!open || !isMobile || !enabled) return;
        ensureListener();

        const token = nextToken++;
        const entry: StackEntry = {
            token,
            close: () => onCloseRef.current(),
        };
        modalStack.push(entry);
        window.history.pushState({ [HISTORY_STATE_KEY]: token }, "");

        return () => {
            const idx = modalStack.indexOf(entry);
            if (idx >= 0) modalStack.splice(idx, 1);

            if (externallyClosed.has(token)) {
                externallyClosed.delete(token);
                return;
            }

            const state = window.history.state as
                | Record<string, unknown>
                | null;
            if (state && state[HISTORY_STATE_KEY] === token) {
                programmaticCloseToken = token;
                window.history.back();
            }
        };
    }, [open, isMobile, enabled]);
}
